import { type TableChoiceMemory, tableChoiceResponseSchema } from "@app/protocol";
import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE } from "../config";
import { useStore } from "../store";
import { rememberTable, type TableSetup } from "./tableMemory";

/**
 * The table this player set up last (slice 0038): `memory` is null until the server answered or
 * when nothing is kept; `save` stores a table as it opens, best effort.
 */
export function useTableMemory(): {
  memory: TableChoiceMemory | null;
  save: (setup: TableSetup) => void;
} {
  const session = useStore((s) => s.session);
  const [memory, setMemory] = useState<TableChoiceMemory | null>(null);
  const latest = useRef<TableChoiceMemory | null>(null);
  useEffect(() => {
    if (session === null) return;
    let live = true;
    fetch(`${API_BASE}/me/table`, { headers: { Authorization: `Bearer ${session}` } })
      .then(async (response) =>
        response.ok ? tableChoiceResponseSchema.safeParse(await response.json()) : null,
      )
      .then((parsed) => {
        if (!live || !parsed?.success || parsed.data.choice === null) return;
        latest.current = parsed.data.choice;
        setMemory(parsed.data.choice);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [session]);
  const save = useCallback(
    (setup: TableSetup) => {
      if (session === null) return;
      const next = rememberTable(latest.current, setup);
      latest.current = next;
      void fetch(`${API_BASE}/me/table`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${session}`, "Content-Type": "application/json" },
        body: JSON.stringify(next),
      }).catch(() => undefined);
    },
    [session],
  );
  return { memory, save };
}
